import { Component, Input, OnChanges, OnInit, SimpleChange, SimpleChanges } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { DocContentsService } from '../../../services/doc-contents.service';
import { BehaviorSubject, combineLatest, map, Observable, of } from 'rxjs';
import * as Diff2Html from 'diff2html';
import { createPatch } from 'diff';
import { DiffFile } from '@models/review/diff-file.model';
import { ColorSchemeType } from 'diff2html/lib/types';
import { LexbeThemeService } from '@services/lexbe-theme.service';

@Component({
    selector: 'lex-file-compare',
    templateUrl: './file-compare.component.html',
    styleUrls: ['./file-compare.component.scss'],
    standalone: false
})
export class FileCompareComponent implements OnInit, OnChanges {
	@Input() docId: string;
	@Input() diffDocId: string;
	@Input() mainTabSelectedIndex = 10;

	outputHtml$: Observable<string> = of('');
	contentObservable: BehaviorSubject<DiffFile> = new BehaviorSubject<DiffFile>({
		leftDocId: '',
		leftContent: '',
		rightDocId: '',
		rightContent: ''
	});
	contentObservable$: Observable<DiffFile> = this.contentObservable.asObservable();

	docText: string;
	diffDocText: string;

	constructor(
		private route: ActivatedRoute,
		private docContentsService: DocContentsService,
		private themeService: LexbeThemeService) { }

	ngOnInit() {
		// If the input properties weren't specified, we're probably loading this from an iframe - in which case IDs will be in the query params
		if (!this.docId && !this.diffDocId) {
			this.docId = this.route.snapshot.queryParams['DocId'];
			this.diffDocId = this.route.snapshot.queryParams['DiffDocId'];

			// ngOnChanges is not called when viewed in an iframe - call it once here to initialize the diff texts
			this.ngOnChanges({
				docId: new SimpleChange(undefined, this.docId, true),
				diffDocId: new SimpleChange(undefined, this.diffDocId, true)
			});
		}

		this.outputHtml$ = combineLatest([this.contentObservable$, this.themeService.isDarkMode$])
			.pipe(map(([content, isDarkMode]) => {
				const oldString = content.leftContent;
				const newString = content.rightContent;
				if (!oldString || !newString) return '';
				const unifiedDiff = createPatch('', oldString, newString, `Left Document ID: ${content.leftDocId}`, `Right Document ID: ${content.rightDocId}`, {context: Infinity});
				return Diff2Html.html(Diff2Html.parse(unifiedDiff), {
					outputFormat: 'side-by-side',
					drawFileList: false,
					matching: 'lines',
					colorScheme: (isDarkMode ? 'dark':'light') as ColorSchemeType
				});
			}));
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (this.mainTabSelectedIndex !== 10) return;

		if (!this.docText || changes.docId && changes.docId.currentValue) {
			this.docContentsService.getContentTextForDiffing(this.docId).subscribe((docContent) => {
				this.docText = docContent.textForDiffing;
				this.updateContent();
			});
		}

		if (!this.diffDocText || changes.diffDocId && changes.diffDocId.currentValue) {
			this.docContentsService.getContentTextForDiffing(this.diffDocId).subscribe((docContent) => {
				this.diffDocText = docContent.textForDiffing;
				this.updateContent();
			});
		}
	}

	private updateContent() {
		this.contentObservable.next({
			leftDocId: this.docId,
			leftContent: this.docText,
			rightDocId: this.diffDocId,
			rightContent: this.diffDocText
		});
	}
}
